import {Collaborator, Permissions} from 'auth/PermissionConstants';
import * as PermissionDAO from 'auth/PermissionDAO';
import {ChemDrawStoichiometryData} from 'chemistry/ChemistryConstants';
import {bulkFetchChemDrawStoichiometryData} from 'chemistry/ChemistryDAO';
import {FolderListingExtras, FolderWithPermissions} from 'constants/FolderConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {Batch, Container, SampleDetails, Storable, StorableSummary} from 'constants/SampleConstants';
import {EntitySummary, User} from 'constants/UserConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FolderDAO from 'file-browser/FolderDAO';
import SourceDAO from 'file-browser/SourceDAO';
import {BioentitySummary} from 'folder-items/FileConstants';
import FolderListingExtrasManager from 'folders/FolderListingExtrasManager';
import {BatchModelManager} from 'models/managers/BatchModelManager';
import BioentityModelManager from 'models/managers/BioentityModelManager';
import {EntitySummaryModelManager} from 'models/managers/EntitySummaryModelManager';
import {ProtocolSummary} from 'notebook/ELNConstants';
// @ts-expect-error
import EditorActions from 'notebook/protocols/actions/EditorActions';
import {ReviewChangeTimestamps} from 'notebook/reviews/ReviewConstants';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import {doBatchedBulkLookupColumns} from 'notebook/structured-tables/lookup-columns/BatchedBulkLookupColumns';
import {BulkLookupResult} from 'notebook/structured-tables/lookup-columns/LookupColumnsDAO';
import BioentityAliasInfosModelManager from 'registry/BioentityAliasInfosModelManager';
import SchemaFieldSelectorModelManager from 'registry/schema-field-selector/SchemaFieldSelectorModelManager';
import {HydratedFulfillment} from 'requests/fulfillments/HydratedFulfillmentConstants';
import AllRequestTaskSchemasModelManager from 'requests/models/AllRequestTaskSchemasModelManager';
import RequestFulfillmentsModelManager from 'requests/models/RequestFulfillmentsModelManager';
import RequestModelManager from 'requests/models/RequestModelManager';
import RequestSampleGroupsModelManager from 'requests/models/RequestSampleGroupsModelManager';
import RequestTasksModelManager from 'requests/models/RequestTasksModelManager';
import {Request, RequestFullfillableStatuses} from 'requests/RequestConstants';
import {getFulfillableStatuses} from 'requests/RequestDAO';
import {RequestTask, RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import {bulkFetchAssayResultValidation, bulkFetchAssayRunValidation} from 'results/assays/AssayRecordDAO';
import {AssayResultValidation, AssayRunValidation} from 'results/validation/ValidationConstants';
import StageRunSampleModelManager from 'samples/StageRunSampleModelManager';
import StorableSummaryModelManager from 'samples/storables/StorableSummaryModelManager';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import {SearchResults} from 'search/SearchConstants';
import SearchResultsModelManager from 'search/SearchResultsModelManager';
import lazyInit from 'util/lazyInit';
import AllWorkflowTemplatesModelManager from 'workflows/AllWorkflowTemplatesModelManager';
import * as WorkflowDAO from 'workflows/dao/WorkflowDAO';
import * as StageEntryDAO from 'workflows/stage-entries/StageEntryDAO';
import {
  SampleGroup,
  StageEntrySummary,
  WorkflowDashboardDetails,
  WorkflowTemplate,
} from 'workflows/WorkflowConstants';
import {WorklistSummary} from 'worklists/WorklistConstants';
import * as WorklistDAO from 'worklists/WorklistDAO';
import {worklistBulkItemsResponseTransform} from 'worklists/WorklistUtil';

import {ContainerModelManager} from './managers/ContainerModelManager';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import ManagedAllModelManager from './managers/ManagedAllModelManager';
import ManagedModelManager from './managers/ManagedModelManager';
import {ModelManager} from './managers/ModelManager';
import StorableModelManager from './managers/StorableModelManager';
import ValidationExtrasModelManager from './managers/ValidationExtrasManager';

/**
 * After a new model is added, requestFetch can be called at any point to intelligently fetch the model.
 *
 * To add a new managed model:
 * (1) Declare it in ManagedModelTypes
 * (2) Map the new model to a ModelManger in getModelManagers
 *     - Use ManagedModelManager if each API call returns the same JSON representation as the Redux store.
 *       This will automatically store the model in state.models.managed[model type][model key]
 *     - For more complex endpoints, use a custom ModelManager.
 */

export enum ManagedModelType {
  ALL_WORKFLOW_TEMPLATES = 'allWorkflowTemplates',
  ALL_REQUEST_TASK_SCHEMAS = 'allRequestTaskSchemas',
  ASSAY_RESULT_VALIDATION = 'assayResultValidation',
  ASSAY_RUN_VALIDATION = 'assayRunValidation',
  AUTHORS_BY_ITEM_ID = 'authorsByItemId',
  BATCH = 'batch',
  BIOENTITY_SUMMARY = 'bioentitySummary',
  BIOENTITY_ALIAS_INFOS_BY_BIOENTITY_ID = 'bioentityAliasInfosByBioentityId',
  BULK_LOOKUP_COLUMN_RESULTS = 'bulkLookupColumnResults',
  CHEM_DRAW_STOICHIOMETRY_DATA = 'chemDrawStoichiometryData',
  CONTAINER = 'container',
  COLLABORATORS_BY_SOURCE_ID = 'collaboratorsBySourceId',
  ENTITY_SUMMARY_BY_HANDLE = 'entitySummariesByHandle',
  HYDRATED_FULFILLMENT_BY_ID = 'hydratedFulfillmentsById',
  HYDRATED_STAGE_RUN_SAMPLE_BY_ID = 'hydratedStageRunSampleById',
  ENTRY_REVIEW_CHANGE_TIMESTAMPS_BY_ENTRY_ID = 'entryReviewChangeTimestampsByEntryId',
  FOLDER_BY_ID = 'folderById',
  FOLDER_LISTING_EXTRAS_BY_FOLDER_ID = 'folderListingExtrasByFolderId',
  PERMISSIONS_BY_ITEM_ID = 'permissionsByItemId',
  PROTOCOL_SUMMARY = 'protocolSummary',
  REQUEST_BY_ID = 'requestById',
  REQUEST_TASKS_BY_REQUEST_ID = 'requestTasksByRequestId',
  FULFILLABLE_STATUSES_BY_REQUEST_ID = 'fulfillableStatusesByRequestId',
  SAMPLE_GROUPS_BY_REQUEST_ID = 'sampleGroupsByRequestId',
  SEARCH_RESULTS = 'searchResults',
  SCHEMA_FIELD_SELECTOR_BY_ID = 'selectorById',
  STAGE_ENTRY_SUMMARY = 'stageEntrySummary',
  STORABLE = 'storable',
  STORABLE_SUMMARY = 'storableSummary',
  VALIDATION_EXTRAS_BY_BIOENTITY_ID = 'validationExtrasByBioentityId',
  WORKFLOW_DASHBOARD_DETAILS = 'workflowDashboardDetails',
  WORKLIST_SUMMARIES_BY_ID = 'worklistSummariesById',
  WORKLIST_ITEMS_BY_WORKLIST_ID = 'worklistItemsByWorklistId',
}

export interface ManagedModelTypes {
  [ManagedModelType.BULK_LOOKUP_COLUMN_RESULTS]: BulkLookupResult;
  [ManagedModelType.ALL_WORKFLOW_TEMPLATES]: Array<WorkflowTemplate>;
  [ManagedModelType.ALL_REQUEST_TASK_SCHEMAS]: Array<RequestTaskSchema>;
  [ManagedModelType.ASSAY_RESULT_VALIDATION]: AssayResultValidation;
  [ManagedModelType.ASSAY_RUN_VALIDATION]: AssayRunValidation;
  [ManagedModelType.AUTHORS_BY_ITEM_ID]: Array<User>;
  [ManagedModelType.BATCH]: Batch;
  [ManagedModelType.BIOENTITY_SUMMARY]: BioentitySummary;
  [ManagedModelType.BIOENTITY_ALIAS_INFOS_BY_BIOENTITY_ID]: Array<BioentityAliasInfo>;
  // Extracted table results of a ChemDraw file, keyed by NoteLinkedObject ID (id in the to_dict,
  // uuid on the server).
  [ManagedModelType.CHEM_DRAW_STOICHIOMETRY_DATA]: ChemDrawStoichiometryData;
  [ManagedModelType.COLLABORATORS_BY_SOURCE_ID]: Array<Collaborator>;
  [ManagedModelType.CONTAINER]: Container;
  [ManagedModelType.ENTITY_SUMMARY_BY_HANDLE]: EntitySummary;
  [ManagedModelType.HYDRATED_FULFILLMENT_BY_ID]: HydratedFulfillment;
  [ManagedModelType.HYDRATED_STAGE_RUN_SAMPLE_BY_ID]: SampleDetails;
  [ManagedModelType.ENTRY_REVIEW_CHANGE_TIMESTAMPS_BY_ENTRY_ID]: ReviewChangeTimestamps;
  [ManagedModelType.FOLDER_BY_ID]: FolderWithPermissions;
  [ManagedModelType.FOLDER_LISTING_EXTRAS_BY_FOLDER_ID]: FolderListingExtras;
  [ManagedModelType.FULFILLABLE_STATUSES_BY_REQUEST_ID]: RequestFullfillableStatuses;
  [ManagedModelType.PERMISSIONS_BY_ITEM_ID]: Permissions;
  [ManagedModelType.PROTOCOL_SUMMARY]: ProtocolSummary;
  [ManagedModelType.REQUEST_BY_ID]: Request;
  // Request tasks keyed by Request ID.
  [ManagedModelType.REQUEST_TASKS_BY_REQUEST_ID]: Array<RequestTask>;
  [ManagedModelType.SAMPLE_GROUPS_BY_REQUEST_ID]: Array<SampleGroup>;
  [ManagedModelType.SEARCH_RESULTS]: SearchResults;
  [ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID]: SchemaFieldSelector;
  [ManagedModelType.STAGE_ENTRY_SUMMARY]: StageEntrySummary;
  [ManagedModelType.STORABLE]: Storable;
  [ManagedModelType.STORABLE_SUMMARY]: StorableSummary;
  [ManagedModelType.VALIDATION_EXTRAS_BY_BIOENTITY_ID]: ValidationExtras;
  [ManagedModelType.WORKFLOW_DASHBOARD_DETAILS]: WorkflowDashboardDetails;
  [ManagedModelType.WORKLIST_SUMMARIES_BY_ID]: WorklistSummary;
  [ManagedModelType.WORKLIST_ITEMS_BY_WORKLIST_ID]: Array<string>;
}

const getModelManagers = lazyInit<{[modelType in keyof ManagedModelTypes]: ModelManager<modelType>}>(() => ({
  [ManagedModelType.BULK_LOOKUP_COLUMN_RESULTS]: new ManagedModelManager(
    ManagedModelType.BULK_LOOKUP_COLUMN_RESULTS,
    doBatchedBulkLookupColumns
  ),
  [ManagedModelType.ALL_REQUEST_TASK_SCHEMAS]: new AllRequestTaskSchemasModelManager(),
  [ManagedModelType.ALL_WORKFLOW_TEMPLATES]: new AllWorkflowTemplatesModelManager(),
  [ManagedModelType.ASSAY_RESULT_VALIDATION]: new ManagedModelManager(
    ManagedModelType.ASSAY_RESULT_VALIDATION,
    bulkFetchAssayResultValidation
  ),
  [ManagedModelType.ASSAY_RUN_VALIDATION]: new ManagedModelManager(
    ManagedModelType.ASSAY_RUN_VALIDATION,
    bulkFetchAssayRunValidation
  ),
  [ManagedModelType.AUTHORS_BY_ITEM_ID]: new ManagedModelManager(
    ManagedModelType.AUTHORS_BY_ITEM_ID,
    FileBrowserDAO.bulkGetAuthors
  ),
  [ManagedModelType.BATCH]: new BatchModelManager(),
  [ManagedModelType.BIOENTITY_SUMMARY]: new BioentityModelManager(),
  [ManagedModelType.BIOENTITY_ALIAS_INFOS_BY_BIOENTITY_ID]: new BioentityAliasInfosModelManager(),
  [ManagedModelType.CHEM_DRAW_STOICHIOMETRY_DATA]: new ManagedModelManager(
    ManagedModelType.CHEM_DRAW_STOICHIOMETRY_DATA,
    bulkFetchChemDrawStoichiometryData
  ),
  [ManagedModelType.COLLABORATORS_BY_SOURCE_ID]: new ManagedModelManager(
    ManagedModelType.COLLABORATORS_BY_SOURCE_ID,
    SourceDAO.bulkFetchCollaborators
  ),
  [ManagedModelType.CONTAINER]: new ContainerModelManager(),
  [ManagedModelType.ENTITY_SUMMARY_BY_HANDLE]: new EntitySummaryModelManager(),
  [ManagedModelType.HYDRATED_FULFILLMENT_BY_ID]: new RequestFulfillmentsModelManager(),
  [ManagedModelType.HYDRATED_STAGE_RUN_SAMPLE_BY_ID]: new StageRunSampleModelManager(),
  [ManagedModelType.ENTRY_REVIEW_CHANGE_TIMESTAMPS_BY_ENTRY_ID]: new ManagedModelManager(
    ManagedModelType.ENTRY_REVIEW_CHANGE_TIMESTAMPS_BY_ENTRY_ID,
    ReviewDAO.bulkGetReviewChangeTimestamps
  ),
  [ManagedModelType.FOLDER_BY_ID]: new ManagedModelManager(
    ManagedModelType.FOLDER_BY_ID,
    FolderDAO.bulkFetch
  ),
  [ManagedModelType.FOLDER_LISTING_EXTRAS_BY_FOLDER_ID]: new FolderListingExtrasManager(),
  [ManagedModelType.WORKFLOW_DASHBOARD_DETAILS]: new ManagedModelManager(
    ManagedModelType.WORKFLOW_DASHBOARD_DETAILS,
    WorkflowDAO.bulkFetchWorkflowDashboardDetails
  ),
  [ManagedModelType.PERMISSIONS_BY_ITEM_ID]: new ManagedModelManager(
    ManagedModelType.PERMISSIONS_BY_ITEM_ID,
    PermissionDAO.bulkFetchPermissions
  ),
  [ManagedModelType.FULFILLABLE_STATUSES_BY_REQUEST_ID]: new ManagedModelManager(
    ManagedModelType.FULFILLABLE_STATUSES_BY_REQUEST_ID,
    getFulfillableStatuses
  ),
  [ManagedModelType.PROTOCOL_SUMMARY]: new ManagedModelManager(
    ManagedModelType.PROTOCOL_SUMMARY,
    EditorActions.bulkFetchSummaries
  ),
  [ManagedModelType.REQUEST_BY_ID]: new RequestModelManager(),
  [ManagedModelType.REQUEST_TASKS_BY_REQUEST_ID]: new RequestTasksModelManager(),
  [ManagedModelType.SAMPLE_GROUPS_BY_REQUEST_ID]: new RequestSampleGroupsModelManager(),
  [ManagedModelType.SEARCH_RESULTS]: new SearchResultsModelManager(),
  [ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID]: new SchemaFieldSelectorModelManager(),
  [ManagedModelType.STAGE_ENTRY_SUMMARY]: new ManagedModelManager(
    ManagedModelType.STAGE_ENTRY_SUMMARY,
    StageEntryDAO.bulkFetchSummaries
  ),
  [ManagedModelType.STORABLE]: new StorableModelManager(),
  [ManagedModelType.STORABLE_SUMMARY]: new StorableSummaryModelManager(),
  [ManagedModelType.VALIDATION_EXTRAS_BY_BIOENTITY_ID]: new ValidationExtrasModelManager(),
  [ManagedModelType.WORKLIST_SUMMARIES_BY_ID]: new ManagedModelManager(
    ManagedModelType.WORKLIST_SUMMARIES_BY_ID,
    WorklistDAO.bulkFetchWorklistSummaries
  ),
  [ManagedModelType.WORKLIST_ITEMS_BY_WORKLIST_ID]: new ManagedModelManager(
    ManagedModelType.WORKLIST_ITEMS_BY_WORKLIST_ID,
    async (worklistIds) =>
      worklistBulkItemsResponseTransform(await WorklistDAO.bulkFetchWorklistItemIds(worklistIds))
  ),
}));

export function getModelManager<T extends ManagedModelType>(modelType: T): ModelManager<T> {
  // This is technically unsafe because if T is a union of multiple model types, indexing into
  // `getModelManagers()` will get a union of model manager types rather than just one, but that's obscure
  // enough that we ignore it.
  return getModelManagers()[modelType] as unknown as ModelManager<T>;
}
